const JPEG_TYPES = new Set(['image/jpeg', 'image/jpg']);
const MAX_DIMENSION = 2048;
const JPEG_QUALITY = 0.88;

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () =>
      reject(new Error('Impossible de lire cette image sur votre téléphone.'));
    image.src = src;
  });
}

function scaleDimensions(
  width: number,
  height: number,
): { width: number; height: number } {
  const longestEdge = Math.max(width, height);
  if (longestEdge <= MAX_DIMENSION) {
    return { width, height };
  }

  const ratio = MAX_DIMENSION / longestEdge;
  return {
    width: Math.round(width * ratio),
    height: Math.round(height * ratio),
  };
}

function rasterizeToJpeg(source: File, image: HTMLImageElement): Promise<File> {
  const { width, height } = scaleDimensions(
    image.naturalWidth,
    image.naturalHeight,
  );
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext('2d');
  if (!context) {
    return Promise.reject(
      new Error('Impossible de préparer l’image pour l’envoi.'),
    );
  }

  context.drawImage(image, 0, 0, width, height);

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Impossible de convertir l’image.'));
          return;
        }

        const stem = source.name.replace(/\.[^.]+$/, '') || 'photo';
        resolve(
          new File([blob], `${stem}.jpg`, {
            lastModified: Date.now(),
            type: 'image/jpeg',
          }),
        );
      },
      'image/jpeg',
      JPEG_QUALITY,
    );
  });
}

/**
 * iPhone photos are often HEIC — Django/Pillow rejects them unless converted.
 * Also downscales very large camera images for reliable mobile upload.
 */
export async function normalizePhotoForUpload(file: File): Promise<File> {
  const objectUrl = URL.createObjectURL(file);

  try {
    const image = await loadImage(objectUrl);
    const isJpeg = JPEG_TYPES.has(file.type.toLowerCase());
    const needsResize =
      Math.max(image.naturalWidth, image.naturalHeight) > MAX_DIMENSION;

    if (isJpeg && !needsResize) {
      return file;
    }

    return rasterizeToJpeg(file, image);
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}
